/** One cached answer: the promise (shared while in flight) and when it was asked for. */
interface Entry<T> {
  at: number;
  value: Promise<T>;
}

const entries = new Map<string, Entry<unknown>>();

/**
 * Asks once per key within `ttlMs` and shares the answer: concurrent callers wait on the same
 * request, and later ones reuse it, so remounts, reopened estimates and undo don't ask again.
 * Lives for the page; a failed request isn't kept.
 * @param key - What is being asked, e.g. `hotel:lisbon|LIS`.
 * @param ttlMs - How long an answer is reused.
 * @param ask - Makes the request.
 * @returns The shared answer.
 * @throws Whatever `ask` throws.
 */
export function cached<T>(key: string, ttlMs: number, ask: () => Promise<T>): Promise<T> {
  const hit = entries.get(key) as Entry<T> | undefined;
  if (hit && Date.now() - hit.at < ttlMs) return hit.value;
  const value = ask();
  entries.set(key, { at: Date.now(), value });
  value.catch(() => {
    if (entries.get(key)?.value === value) entries.delete(key);
  });
  return value;
}

const KEPT_PREFIX = "estimator-cache:";

/**
 * Forgets cached and kept answers whose key starts with `prefix`, e.g. on Refresh.
 * @param prefix - Key prefix.
 * @returns Nothing.
 * @throws Never.
 */
export function forget(prefix: string): void {
  for (const key of entries.keys()) if (key.startsWith(prefix)) entries.delete(key);
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith(KEPT_PREFIX + prefix)) window.localStorage.removeItem(key);
    }
  } catch {
    // Storage blocked: nothing was kept.
  }
}

/**
 * Keeps an answer in this browser across reloads, for {@link kept}.
 * @param key - What was asked, as for {@link cached}.
 * @param value - The answer; must survive JSON.
 * @returns Nothing.
 * @throws Never.
 */
export function keep<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(KEPT_PREFIX + key, JSON.stringify({ at: Date.now(), value }));
  } catch {
    // Storage blocked or full: the answer just isn't kept.
  }
}

/**
 * The answer kept for a key by an earlier visit, however old, with when it was asked for: shown
 * at once while a fresh one is read.
 * @param key - What was asked.
 * @returns The kept answer, or null.
 * @throws Never.
 */
export function kept<T>(key: string): { at: number; value: T } | null {
  try {
    const raw = window.localStorage.getItem(KEPT_PREFIX + key);
    return raw ? (JSON.parse(raw) as { at: number; value: T }) : null;
  } catch {
    return null;
  }
}
